import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ProductGallery } from '../components/product/ProductGallery';
import { ProductAssetSlot } from '../components/product/ProductAssetSlot';
import { WarrantyTag } from '../components/ui/WarrantyTag';
import { ProductRating } from '../components/reviews/ProductRating';
import { PendingNote } from '../components/ui/PendingNote';
import { m1ProSpecs } from '../data/specs';
import { pricingConfig } from '../data/pricing';
import { useLanguage } from '../contexts/LanguageContext';
import { formatMoney, priceBundle } from '../utils/pricing';
import { buttonClasses } from '../utils/buttonStyles';
import { track } from '../utils/analytics';
import { packagingContents, packagingDesign } from '../data/packaging';
const highlights = [{
  title: 'Wireless',
  body: 'A self-contained light. No cable runs to the water.'
}, {
  title: 'Rechargeable',
  body: 'Internal battery with USB-C charging behind a blue protective cover.'
}, {
  title: 'Magnetic',
  body: 'Seats onto its matched-diameter Magnetic Mount System base.'
}, {
  title: 'RGB',
  body: 'Color options from deep electric blue to full-spectrum.'
}];
export function M1Pro() {
  const {
    lang
  } = useLanguage();
  const minPer = Math.min(...pricingConfig.bundles.map(b => priceBundle(b).perUnit));
  useEffect(() => {
    track('view_content', {
      content_name: 'M1 PRO',
      content_ids: [pricingConfig.bundles[0].variantId],
      content_type: 'product',
      value: pricingConfig.unitPrice,
      currency: pricingConfig.currency
    });
  }, []);
  return <>
      <section className="mx-auto grid max-w-site gap-12 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-[1.1fr_1fr] lg:gap-20" data-id="element-1184" data-mp="20mrnzjeqs2yw">
        <ProductGallery data-id="element-1185" data-mp="1vlvb4gcex6hc" />
        <div className="lg:pt-6" data-id="element-1186" data-mp="1vpj7dn98s6zr">
          <ProductRating size="md" className="mb-4" data-id="element-1187" data-mp="3c1594gi424b8" />
          <p className="font-display text-lg uppercase tracking-[0.14em] text-ice" data-id="element-1188" data-mp="3bztxjuifbs9k">Professional-Grade Lighting. Reimagined.</p>
          <h1 className="mt-2 font-display text-7xl font-semibold uppercase leading-[0.9] md:text-8xl" data-id="element-1189" data-mp="24p5vhrn33a9a">M1 PRO</h1>
          <p className="mt-2 text-xl text-white" data-id="element-1190" data-mp="29nqx9vhzi1zu">Wireless RGB Lighting System</p>
          <p className="mt-6 max-w-lg text-lg text-titanium" data-id="element-1191" data-mp="2dpuaopo7rgnr">
            A palm-sized, professional-grade light with 13 LED lenses in a white ABS housing. Rechargeable, wireless and
            magnetically mounted — no wires, no drilling, no complicated installation.
          </p>

          <div className="mt-8 border-y mf-hairline py-6" data-id="element-1192" data-mp="1lwl9mu5sy5ez">
            <p className="text-sm text-titanium" data-id="element-1193" data-mp="1q12p4h54pwjy">Starting at</p>
            <p className="text-3xl font-semibold" data-id="element-1194" data-mp="1uzmaogft96bs">{formatMoney(pricingConfig.unitPrice, lang)}</p>
            <p className="mt-1 text-sm text-ice" data-id="element-1195" data-mp="1zpszohe8isuk">
              As low as {formatMoney(minPer, lang)} per light in a Complete Lighting System
            </p>
            <div className="mt-4 space-y-1.5" data-id="element-1196" data-mp="12af36rydp6ee">
              <WarrantyTag tier="oneYear" data-id="element-1197" data-mp="1799uyjebq5an" />
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-1198" data-mp="zq8zmldofhyd">
            <Link to="/systems" className={`${buttonClasses('primary', 'lg')} flex-1`} data-id="element-1199" data-mp="zpqwd4slk3gy">
              Complete Systems
            </Link>
            <Link to="/build" className={`${buttonClasses('secondary', 'lg')} flex-1`} data-id="element-1200" data-mp="ps2y44vs8du1">
              Build Your Own
            </Link>
          </div>

          <dl className="mt-10 grid grid-cols-2 gap-x-8 gap-y-6" data-id="element-1201" data-mp="lppwnso2j232">
            {highlights.map(h => <div key={h.title} data-id="element-1202" data-mp="gsze8yosjueh">
                <dt className="font-display text-xl font-semibold uppercase" data-id="element-1203" data-mp="c0oepwxyqamg">{h.title}</dt>
                <dd className="mt-1 text-sm text-titanium" data-id="element-1204" data-mp="byv5c9vytcar">{h.body}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      <section aria-labelledby="specs-title" className="border-t mf-hairline bg-navy" data-id="element-1205" data-mp="23jrruwamb4l">
        <div className="mx-auto grid max-w-site gap-10 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-[1fr_1.5fr]" data-id="element-1206" data-mp="3u6klw5by0nyo">
          <div data-id="element-1207" data-mp="3p89ee194epjm">
            <h2 id="specs-title" className="font-display text-5xl font-semibold uppercase leading-[0.95]" data-id="element-1208" data-mp="3p7q6zeh3jjju">
              Technical Specifications
            </h2>
            <p className="mt-5 max-w-md text-titanium" data-id="element-1209" data-mp="1x66nd5rnyrd5">
              Manufacturer-advertised specifications. Actual run time depends on color, brightness mode and water
              temperature.
            </p>
          </div>
          <table className="w-full text-left" data-id="element-1210" data-mp="1oc21l0re064h">
            <caption className="sr-only" data-id="element-1211" data-mp="19g2mh65v15pu">M1 PRO technical specifications</caption>
            <tbody data-id="element-1212" data-mp="19i704ftk9j3b">
              {m1ProSpecs.map(s => <tr key={s.label} className="border-b mf-hairline" data-id="element-1213" data-mp="1efypp36qgt4c">
                  <th scope="row" className="py-4 pr-4 text-sm font-medium text-titanium" data-id="element-1214" data-mp="10fxp7oudrnpe">
                    {lang === 'es' ? s.labelEs : s.label}
                  </th>
                  <td className="py-4 text-white" data-id="element-1215" data-mp="15gbytbbmr5pj">
                    {s.value}
                    {s.status === 'supplier' && <PendingNote data-id="element-1216" data-mp="2zsi8u6ssfgg9">Supplier</PendingNote>}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="pack-title" className="bg-deep" data-id="element-1217" data-mp="39ucjv8argw5j">
        <div className="mx-auto grid max-w-site items-center gap-10 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-2" data-id="element-1218" data-mp="34yneupfz2n1u">
          <ProductAssetSlot view="packaging" tone="studio" className="aspect-[4/3] w-full" data-id="element-1219" data-mp="3iqbpeoakp3gp" />
          <div data-id="element-1220" data-mp="3dwh9lvy398mw">
            <h2 id="pack-title" className="font-display text-5xl font-semibold uppercase leading-[0.95]" data-id="element-1221" data-mp="3dwqy1f3hj7qh">
              Packaged Like Professional Equipment.
            </h2>
            <p className="mt-5 max-w-lg text-titanium" data-id="element-1222" data-mp="3nn26ney4653p">
              Each M1 PRO arrives in MarineForceLED's premium presentation box — protected for transport and presented the way
              professional-grade equipment should be.
            </p>
            <div className="mt-8 grid gap-8 sm:grid-cols-2" data-id="element-1223" data-mp="279lnvih3gztf">
              <div data-id="element-1224" data-mp="279vixkgzxx5z">
                <h3 className="text-sm font-semibold text-white" data-id="element-1225" data-mp="27bez7b2nk87r">Box design</h3>
                <ul className="mt-3 space-y-2 text-sm text-titanium" data-id="element-1226" data-mp="239tdd0854jlf">
                  {packagingDesign.map(d => <li key={d} data-id="element-1227" data-mp="69qmlx8rwk6f">{d}</li>)}
                </ul>
              </div>
              <div data-id="element-1228" data-mp="b8a6zt7pxnwo">
                <h3 className="text-sm font-semibold text-white" data-id="element-1229" data-mp="g5tpj5bg8muf">In the box</h3>
                <ul className="mt-3 space-y-2 text-sm text-titanium" data-id="element-1230" data-mp="g7wj1zbt8y3w">
                  {packagingContents.map(c => <li key={c.item} data-id="element-1231" data-mp="p6rsdka5iobf">
                      {c.item}
                      {c.status === 'supplier-pending' && <PendingNote data-id="element-1232" data-mp="u4jjic2z26ek">Supplier TBC</PendingNote>}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}